import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import {
    FacilityDetailsInfo,
    FacilitySubmittalsGridItem,
    FacilityPlacementsGridItem,
    FacilityProfileDetailsInfo,
    FacilityContractTypes,
    FacilityContractFlags,
    FacilityContractRules,
    FacilityContractInsurance,
    FacilityBillingRate,
    FacilityBillingRules,
    BillingAccountsReceivable,
    BillingInvoicing,
    BillingInvoicingOptions,
    BillingAccountReceivableOptions,
    FacilityProfilesPermissions,
    FacilityAccountDetailsInfo,
    FacilityPayrollDocument,
    FacilityCostCenter,
    FacilityProfilesDocument,
    FacilityProfilePayrollOptions,
    FacilityProfilePayroll,
    FacilityAccountSendToClientContactInfo
} from 'src/app/facility-profiles/models';
import { FacilityCopyInvoicing } from 'src/app/facility-profiles/models/facility-copy-invoicing.model';
import { FacilityCopyPayroll } from 'src/app/facility-profiles/models/facility-copy-payroll.model';
import { DocumentService } from 'src/app/core/document.service';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { appendQuery, combine, getQueryString } from 'src/app/shared/utilities/route';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { HospitalType } from '../shared/lib/facility-rate/models/hospital-types.enum';
import { FacilityProfileContact } from './models/facility-profile-contact';
import { FacilityJobFulfillmentType } from '../shared/models/facility-job-fulfillment-type.model';
import { serializeToQueryString } from 'src/app/shared/utilities/serialize-to-query-string';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { CorporateDirectoryUser } from '../corporate-directory/models/corporate-directory-user.model';
import { FacilityStateClassification } from '../shared/models/facility.model';
import { FacilityLinkedEntityResult } from '../accountmanagers/submittals/am-submittal-status-update/send-to-client-modal/advanced-send-to-client-portal/models/facility-linked-entity-result.model';
import { FacilityOfferImportantDetails } from 'src/app/facility-contracts/facilities/details/components/facility-profile/models/facility-offer-important-details';
import { FacilityProfileCostCenterConfigModel } from './models/facility-profile-cost-center-config.model';
import { FacilityProfileCostCenterConfigOptions } from './models/facility-profile-cost-center-config-options.model';
import { FacilityProfileCostCenterConfigUpdate } from './models/facility-profile-cost-center-config-update.model';

@Injectable({
    providedIn: 'root'
})
export class FacilityProfilesService {
    private readonly _coreApiPath: string;
    private readonly featureFlag = FeatureFlag;

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _documentService: DocumentService
    ) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'AyaNova');
    }

    getFacilityProfileDetails(id: number): Observable<FacilityProfileDetailsInfo> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/${id}/profile`;
        return this._httpClient.get<FacilityProfileDetailsInfo>(url);
    }

    getFacilityType(id: number): Observable<HospitalType> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/${id}/type`;
        return this._httpClient.get<HospitalType>(url);
    }

    getPermissions(): Observable<FacilityProfilesPermissions> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/profile/permissions`;
        return this._httpClient.get<FacilityProfilesPermissions>(url);
    }

    getFacilityProfileAccountDetails(id: number): Observable<FacilityAccountDetailsInfo> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/${id}/account`;
        return this._httpClient.get<FacilityAccountDetailsInfo>(url);
    }

    getFacilityDetails(id: number): Observable<FacilityDetailsInfo> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/${id}/facility`;
        return this._httpClient.get<FacilityDetailsInfo>(url);
    }

    getDocuments(args: any): Observable<PagingToken<FacilityProfilesDocument[]>> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/documents`;
        return this.postPaged<any>(args.pageSize, args.skip, args.filter, args.sort, url);
    }

    downloadDocument(id: number): Observable<unknown> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/documents/${id}`;
        return this._documentService.getDocumentFile(url);
    }

    getContractTypes(id: number, isSystem: boolean): Observable<FacilityContractTypes> {
        const url = isSystem
            ? `${this._coreApiPath}/facility-contracts/system/${id}/types`
            : `${this._coreApiPath}/facility-contracts/facility/${id}/types`;
        return this._httpClient.get<FacilityContractTypes>(url);
    }

    getContractFlags(id: number, isSystem: boolean): Observable<FacilityContractFlags> {
        const url = isSystem
            ? `${this._coreApiPath}/facility-contracts/system/${id}/flags`
            : `${this._coreApiPath}/facility-contracts/facility/${id}/flags`;
        return this._httpClient.get<FacilityContractFlags>(url);
    }

    getContractRules(id: number, isSystem: boolean): Observable<FacilityContractRules> {
        const url = isSystem
            ? `${this._coreApiPath}/facility-contracts/system/${id}/rules`
            : `${this._coreApiPath}/facility-contracts/facility/${id}/rules`;
        return this._httpClient.get<FacilityContractRules>(url);
    }

    getContractInsurance(id: number, isSystem: boolean): Observable<FacilityContractInsurance> {
        const url = isSystem
            ? `${this._coreApiPath}/facility-contracts/system/${id}/insurance`
            : `${this._coreApiPath}/facility-contracts/facility/${id}/insurance`;
        return this._httpClient.get<FacilityContractInsurance>(url);
    }

    getPayrollDetails(facilityId: number): Observable<FacilityProfilePayroll> {
        const url = `${this._coreApiPath}/facility-payroll/${facilityId}`;
        return this._httpClient.get<FacilityProfilePayroll>(url);
    }

    getPayrollOptions(): Observable<FacilityProfilePayrollOptions> {
        const url = `${this._coreApiPath}/facility-payroll/options`;
        return this._httpClient.get<FacilityProfilePayrollOptions>(url);
    }

    updatePayrollDetails(id: number, data: unknown): Observable<FacilityProfilePayroll> {
        const url = `${this._coreApiPath}/facility-payroll/${id}`;
        return this._httpClient.put<FacilityProfilePayroll>(url, data);
    }

    copyFacilityPayroll(id: number, data: FacilityCopyPayroll) {
        const url = `${this._coreApiPath}/facility-payroll/${id}/facility-copy-payroll`;
        return this._httpClient.post(url, data);
    }

    addBillHoldSystem(system: string): Observable<unknown> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };
        return this._httpClient.post(
            `${this._coreApiPath}/facility-payroll/bill-hold-system`,
            JSON.stringify(system),
            httpOptions
        );
    }

    getPayrollDocuments(args: FetchArgs): Observable<PagingToken<FacilityPayrollDocument[]>> {
        let url = `${this._coreApiPath}/facility-payroll/documents`;

        if (args.filter) {
            if (args.filter.hospId) {
                url = appendQuery(url, `filter.hospId=${args.filter.hospId}`);
            }

            if (args.filter.title) {
                url = appendQuery(url, `filter.title=${args.filter.title}`);
            }
            if (args.filter.name) {
                url = appendQuery(url, `filter.name=${args.filter.name}`);
            }
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sort.field=${args.sort.field}&sort.type=${args.sort.dir || 'asc'}`);
        }

        return this.getPaged<any>(args.pageSize, args.skip, url);
    }

    downloadPayrollDocument(id: number): Observable<unknown> {
        const url = `${this._coreApiPath}/facility-payroll/documents/${id}`;
        return this._documentService.getDocumentFile(url);
    }

    deletePayrollDocument(id: number): Observable<unknown> {
        const url = `${this._coreApiPath}/facility-payroll/documents/${id}`;
        return this._httpClient.delete<unknown>(url);
    }

    uploadPayrollDocument(id: number, fileObj: any): Observable<unknown> {
        const url = `${this._coreApiPath}/facility-payroll/documents`;

        const data = new FormData();
        data.append('hospId', id.toString());
        data.append('file', fileObj.file);
        data.append('title', fileObj.title);

        return this._httpClient.post<unknown>(url, data);
    }

    getCostCenters(args: FetchArgs): Observable<PagingToken<FacilityCostCenter[]>> {
        let url = `${this._coreApiPath}/facility-payroll/cost-centers`;

        if (args.filter) {
            if (args.filter.facilityId) {
                url = appendQuery(url, `filter.facilityId=${args.filter.facilityId}`);
            }

            if (args.filter.name) {
                url = appendQuery(url, `filter.name=${args.filter.name}`);
            }

            if (args.filter.costCenter) {
                url = appendQuery(url, `filter.costCenter=${args.filter.costCenter}`);
            }
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sort.field=${args.sort.field}&sort.type=${args.sort.dir || 'asc'}`);
        }

        return this.getPaged<any>(args.pageSize, args.skip, url);
    }

    updateCostCenter(id: number, data: FacilityCostCenter): Observable<FacilityCostCenter> {
        const url = `${this._coreApiPath}/facility-payroll/cost-centers/${id}`;
        return this._httpClient.put<FacilityCostCenter>(url, data);
    }

    createCostCenter(data: FacilityCostCenter): Observable<FacilityCostCenter> {
        const url = `${this._coreApiPath}/facility-payroll/cost-centers`;
        return this._httpClient.post<FacilityCostCenter>(url, data);
    }

    getBillingRatesByFacilityId(facilityId: number): Observable<FacilityBillingRate[]> {
        const url = `${this._coreApiPath}/facility-billing/${facilityId}/rates`;
        return this._httpClient.get<FacilityBillingRate[]>(url);
    }

    getRulesByFacilityId(facilityId: number): Observable<FacilityBillingRules> {
        const url = `${this._coreApiPath}/facility-billing/${facilityId}/rules`;
        return this._httpClient.get<FacilityBillingRules>(url);
    }

    getFacilityBillingInvoicing(id: number): Observable<BillingInvoicing> {
        const url = `${this._coreApiPath}/facility-billing/${id}/invoicing`;
        return this._httpClient.get<BillingInvoicing>(url);
    }

    getFacilityInvoicingOptions(): Observable<BillingInvoicingOptions> {
        const url = `${this._coreApiPath}/facility-billing/invoicing/options`;
        return this._httpClient.get<BillingInvoicingOptions>(url);
    }

    updateFacilityBillingInvoicing(id: number, data: BillingInvoicing): Observable<BillingInvoicing> {
        const url = `${this._coreApiPath}/facility-billing/${id}/invoicing`;
        return this._httpClient.put<BillingInvoicing>(url, data);
    }

    copyFacilityInvoicing(id: number, data: FacilityCopyInvoicing) {
        const url = `${this._coreApiPath}/facility-billing/${id}/facility-copy-invoicing`;
        return this._httpClient.post(url, data);
    }

    getFacilityBillingAccountsReceivable(id: number): Observable<BillingAccountsReceivable> {
        const url = `${this._coreApiPath}/facility-billing/${id}/accounts-receivable`;
        return this._httpClient.get<BillingAccountsReceivable>(url);
    }

    getSystemAccountsReceivable(systemId: number): Observable<BillingAccountsReceivable> {
        const url = `${this._coreApiPath}/facility-contracts/system/${systemId}/accounts-receivable`;
        return this._httpClient.get<BillingAccountsReceivable>(url);
    }

    updateSystemAccountsReceivable(
        systemId: number,
        data: BillingAccountsReceivable
    ): Observable<BillingAccountsReceivable> {
        const url = `${this._coreApiPath}/facility-contracts/system/${systemId}/accounts-receivable`;
        return this._httpClient.put(url, data) as Observable<BillingAccountsReceivable>;
    }

    getFacilityAccountsReceivableOptions(): Observable<BillingAccountReceivableOptions> {
        const url = `${this._coreApiPath}/facility-billing/accounts-receivable/options`;
        return this._httpClient.get<BillingAccountReceivableOptions>(url);
    }

    updateFacilityBillingAccountsReceivable(
        id: number,
        data: BillingAccountsReceivable
    ): Observable<BillingAccountsReceivable> {
        const url = `${this._coreApiPath}/facility-billing/${id}/accounts-receivable`;
        return this._httpClient.put<BillingAccountsReceivable>(url, data);
    }

    getSubmittals(id: number, args: FetchArgs): Observable<PagingToken<FacilitySubmittalsGridItem[]>> {
        let url = `${this._coreApiPath}/facility/${id}/submittals`;

        if (args.filter) {
            url += getQueryString(args.filter);
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sort.field=${args.sort.field}&sort.type=${args.sort.dir || 'asc'}`);
        }

        return this._httpClient.get<PagingToken<FacilitySubmittalsGridItem[]>>(url);
    }

    getFacilityPlacements(id: number, args: FetchArgs): Observable<PagingToken<FacilityPlacementsGridItem[]>> {
        const queryString = serializeToQueryString(args);
        const url = `${this._coreApiPath}/facility/${id}/placements`;
        return this._httpClient.get<PagingToken<FacilityPlacementsGridItem[]>>(`${url}${queryString}`);
    }

    getMainFacilities(facilityId: number, invoiceCaregiverListingTypeId: number): Observable<Lookup[]> {
        const url = `${this._coreApiPath}/facility-billing/${facilityId}/invoicingType/${invoiceCaregiverListingTypeId}/facilities`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getFacilityProfileContacts(facilityId: number): Observable<FacilityProfileContact[]> {
        const url = `${this._coreApiPath}/facility/${facilityId}/contacts`;
        return this._httpClient.get<FacilityProfileContact[]>(url);
    }

    getFacilityLookupAndUnits(facilityId: number): Observable<FacilityLinkedEntityResult> {
        const url = `${this._coreApiPath}/facility/${facilityId}/related-entities`;
        return this._httpClient.get<FacilityLinkedEntityResult>(url);
    }

    getFacilityJobFulfillmentTypes(facilityId: number): Observable<FacilityJobFulfillmentType[]> {
        const url = `${this._coreApiPath}/facility/${facilityId}/job-fulfillment-types`;
        return this._httpClient.get<FacilityJobFulfillmentType[]>(url);
    }

    upsertFacilityJobFulfillmentTypes(facilityId: number, typesToUpsert: FacilityJobFulfillmentType[]) {
        const url = `${this._coreApiPath}/facility/${facilityId}/job-fulfillment-types`;
        return this._httpClient.post<any>(url, typesToUpsert);
    }

    getVaccineStatuses(): Observable<Lookup[]> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/vaccination-statuses`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getVaccineTypes(): Observable<Lookup[]> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/vaccination-types`;
        return this._httpClient.get<Lookup[]>(url);
    }

    copyOrientationFromSystem(facilityId: number, contractGroupId: number): Observable<any> {
        const url = `${this._coreApiPath}/facility-billing/${facilityId}/copy-accounts-receivable/${contractGroupId}`;
        return this._httpClient.post<any>(url, {});
    }

    getFacilityContractGroupId(facilityId: number): Observable<number> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/${facilityId}/contract-group-id`;
        return this._httpClient.get<number>(url);
    }

    checkFacilityIsConnectEnabled(facilityId: number): Observable<boolean> {
        const url = `${this._coreApiPath}/facility/${facilityId}/connect-enabled`;
        return this._httpClient.get<boolean>(url);
    }

    private postPaged<T>(
        pageSize: number,
        skip: number,
        filter: object,
        sort: object,
        url: string
    ): Observable<PagingToken<T[]>> {
        return this._httpClient.post<PagingToken<T[]>>(url, { pageSize, skip, filter, sort }).pipe(
            map((resp) => {
                return resp;
            })
        );
    }

    private getPaged<T>(pageSize: number, skip: number, url: string): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this._httpClient.get<PagingToken<T[]>>(path, { observe: 'response' }).pipe(
            map((resp) => {
                return resp.body;
            })
        );
    }

    getCorporateDirectoryUsers(employeeName: string | null): Observable<CorporateDirectoryUser[]> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/corporate-directory`;
        return this._httpClient.get<CorporateDirectoryUser[]>(url, { params: { employeeName } });
    }

    updateSendToClientContact(
        data: FacilityAccountSendToClientContactInfo
    ): Observable<FacilityAccountSendToClientContactInfo> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/save-send-to-client-user`;
        return this._httpClient.put<FacilityAccountSendToClientContactInfo>(url, data);
    }

    getSendToClientContact(facilityProfileId: number): Observable<FacilityAccountSendToClientContactInfo> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/send-to-client-contact`;
        return this._httpClient.get<FacilityAccountSendToClientContactInfo>(url, { params: { facilityProfileId } });
    }

    getFacilityStateClassification(state: string, facilityId: number): Observable<FacilityStateClassification | null> {
        const url = combine(this._coreApiPath, 'facility', facilityId.toString(), 'state-classification');

        return this._httpClient.get<FacilityStateClassification>(url, { params: { state } }).pipe(
            map((response: any) => {
                return response ?? null;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    return of(null);
                }
                return throwError(() => error);
            })
        );
    }

    getFacilityProfilesByGlobalKey(globalFacilityKey: string): Observable<FacilityProfileDetailsInfo[]> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/${globalFacilityKey}/profiles`;
        return this._httpClient.get<FacilityProfileDetailsInfo[]>(url);
    }

    updateFacilityOfferImportantDetails(offerImportantDetails: FacilityOfferImportantDetails): Observable<void> {
        const url = `${this._coreApiPath}/facility-contracts/facility-profiles/offer-important-details`;
        return this._httpClient.put<void>(url, offerImportantDetails);
    }

    getCostCenterConfigOptions(): Observable<FacilityProfileCostCenterConfigOptions> {
        const url = `${this._coreApiPath}/facility-payroll/cost-center-config-options`;
        return this._httpClient.get<FacilityProfileCostCenterConfigOptions>(url);
    }

    getCostCenterConfig(facilityId: number): Observable<FacilityProfileCostCenterConfigModel> {
        const url = `${this._coreApiPath}/facility-payroll/${facilityId}/cost-center-config`;
        return this._httpClient.get<FacilityProfileCostCenterConfigModel>(url);
    }

    updateCostCenterConfig(configObj: FacilityProfileCostCenterConfigUpdate): Observable<{}> {
        const url = `${this._coreApiPath}/facility-payroll/cost-center-config`;
        return this._httpClient.put<{}>(url, configObj);
    }
}
